import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import styles from './Admin.module.css';

const TABS = ['Trailers', 'Events', 'Users', 'Submissions', 'Audit Log'];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Trailers');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); }
  }, [user, navigate]);

  if (!user || user.role !== 'admin') return null;

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.header}>
          <p className="label">Admin</p>
          <h1 className="page-title">Platform Management</h1>
        </div>

        <div className={styles.tabs}>
          {TABS.map(t => (
            <button key={t} className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`} onClick={() => setTab(t)}>
              {t}
            </button>
          ))}
        </div>

        <div className={styles.panel}>
          {tab === 'Trailers'    && <AdminTrailers />}
          {tab === 'Users'       && <AdminUsers />}
          {tab === 'Submissions' && <AdminSubmissions />}
          {tab === 'Events'       && <AdminEvents />}
          {tab === 'Audit Log'   && <AdminAuditLog />}
        </div>
      </div>
    </div>
  );
}

// ─── TRAILERS TAB ─────────────────────────────────────────────
function AdminTrailers() {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', filmmakerBio: '', videoUrl: '', thumbnailUrl: '', voteGoal: 10000, genres: [], roundEnd: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const GENRES = ['Action','Thriller','Horror','Comedy','Adventure','Sci-Fi','Family','Musical','Drama'];

  const load = () => {
    setLoading(true);
    api.getTrailers({ status: 'active' })
      .then(setTrailers).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleGenre = g => setForm(f => ({
    ...f,
    genres: f.genres.includes(g) ? f.genres.filter(x => x !== g) : [...f.genres, g]
  }));

  const submit = async e => {
    e.preventDefault();
    setSaving(true); setMessage(null);
    try {
      if (editId) {
        await api.adminUpdateTrailer(editId, { ...form, voteGoal: parseInt(form.voteGoal) });
      } else {
        await api.adminCreateTrailer({ ...form, voteGoal: parseInt(form.voteGoal) });
      }
      setMessage({ type: 'success', text: editId ? 'Trailer updated' : 'Trailer created' });
      setShowForm(false); setEditId(null);
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async id => {
    if (!window.confirm('Deactivate this trailer?')) return;
    await api.adminDeactivateTrailer(id);
    load();
  };

  const startEdit = t => {
    setForm({
      title: t.title, description: t.description || '', filmmakerBio: t.filmmaker_bio || '',
      videoUrl: t.video_url, thumbnailUrl: t.thumbnail_url || '',
      voteGoal: t.vote_goal, genres: t.genres || [], status: t.status,
      roundEnd: t.round_end ? t.round_end.slice(0, 16) : '',
    });
    setEditId(t.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Active Trailers</h2>
        <button className="btn btn-magenta btn-sm" onClick={() => { setShowForm(s => !s); setEditId(null); setForm({ title: '', description: '', filmmakerBio: '', videoUrl: '', thumbnailUrl: '', voteGoal: 10000, genres: [], roundEnd: '', status: 'active' }); }}>
          {showForm ? 'Cancel' : '+ New Trailer'}
        </button>
      </div>

      {message && <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>{message.text}</div>}

      {showForm && (
        <form onSubmit={submit} className={styles.form}>
          <div className={styles.formRow}>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" value={form.title} onChange={set('title')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Vote Goal *</label>
              <input className="form-input" type="number" value={form.voteGoal} onChange={set('voteGoal')} required min="1" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Video URL (YouTube embed) *</label>
            <input className="form-input" value={form.videoUrl} onChange={set('videoUrl')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Thumbnail URL</label>
            <input className="form-input" value={form.thumbnailUrl} onChange={set('thumbnailUrl')} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" value={form.description} onChange={set('description')} rows={3} />
          </div>
          <div className="form-group">
            <label className="form-label">Filmmaker Bio</label>
            <textarea className="form-input" value={form.filmmakerBio} onChange={set('filmmakerBio')} rows={2} />
          </div>
          <div className="form-group">
            <label className="form-label">Round End Date</label>
            <input className="form-input" type="datetime-local" value={form.roundEnd} onChange={set('roundEnd')} />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={set('status')}>
              {['draft','active','goal_reached','in_production','released','inactive'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Genres</label>
            <div className={styles.genreGrid}>
              {GENRES.map(g => (
                <label key={g} className={styles.checkLabel}>
                  <input type="checkbox" checked={form.genres.includes(g)} onChange={() => toggleGenre(g)} />
                  {g}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn btn-magenta" disabled={saving}>
            {saving ? 'Saving…' : editId ? 'Update Trailer' : 'Create Trailer'}
          </button>
        </form>
      )}

      {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Title</th><th>Votes</th><th>Goal</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {trailers.map(t => (
                <tr key={t.id}>
                  <td><strong style={{ color: 'var(--white)' }}>{t.title}</strong></td>
                  <td>{t.vote_count?.toLocaleString()}</td>
                  <td>{t.vote_goal?.toLocaleString()}</td>
                  <td><span className="tag">{t.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-outline-magenta btn-sm" onClick={() => startEdit(t)}>Edit</button>
                      <button className="btn btn-sm" style={{ color: 'var(--coral)' }} onClick={() => deactivate(t.id)}>Deactivate</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── USERS TAB ────────────────────────────────────────────────
function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [walletAdj, setWalletAdj] = useState({ amount: '', reason: '' });
  const [message, setMessage] = useState(null);

  const load = (q) => {
    setLoading(true);
    api.adminGetUsers(q || '').then(setUsers).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  const handleSearch = e => { e.preventDefault(); load(search); };

  const toggleStatus = async (u) => {
    await api.adminSetUserStatus(u.id, { isActive: !u.is_active });
    load(search);
  };

  const adjustWallet = async () => {
    if (!walletAdj.amount || !walletAdj.reason) return;
    try {
      await api.adminAdjustWallet(selected.id, {
        amountCents: parseInt(parseFloat(walletAdj.amount) * 100),
        reason: walletAdj.reason,
      });
      setMessage({ type: 'success', text: 'Wallet adjusted' });
      setWalletAdj({ amount: '', reason: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Users</h2>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ flex: 1, minWidth: 0, maxWidth: 280 }} placeholder="Search email or username"
            value={search} onChange={e => setSearch(e.target.value)} />
          <button type="submit" className="btn btn-outline-magenta btn-sm">Search</button>
        </form>
      </div>

      {message && <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>{message.text}</div>}

      {selected && (
        <div className={styles.userPanel}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>{selected.username} — Wallet Adjustment</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>Close</button>
          </div>
          <div className={styles.formRow}>
            <div className="form-group">
              <label className="form-label">Amount (negative to debit)</label>
              <input className="form-input" type="number" step="0.01"
                value={walletAdj.amount} onChange={e => setWalletAdj(w => ({ ...w, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Reason (required for audit)</label>
              <input className="form-input" value={walletAdj.reason}
                onChange={e => setWalletAdj(w => ({ ...w, reason: e.target.value }))} />
            </div>
          </div>
          <button className="btn btn-magenta btn-sm" onClick={adjustWallet}>Apply Adjustment</button>
        </div>
      )}

      {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Username</th><th>Email</th><th>Role</th><th>Balance</th><th>Active</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <td><strong style={{ color: 'var(--white)' }}>{u.username}</strong></td>
                  <td style={{ color: 'var(--gray)' }}>{u.email}</td>
                  <td><span className={`tag ${u.role === 'admin' ? 'tag-red' : ''}`}>{u.role}</span></td>
                  <td>${((u.balance_cents || 0) / 100).toFixed(2)}</td>
                  <td>{u.is_active ? '✓' : '✗'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-outline-magenta btn-sm" onClick={() => setSelected(u)}>Wallet</button>
                      <button className="btn btn-sm" style={{ color: u.is_active ? 'var(--coral)' : 'var(--teal)' }}
                        onClick={() => toggleStatus(u)}>
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── SUBMISSIONS TAB ──────────────────────────────────────────
function AdminSubmissions() {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', adminNotes: '' });

  const load = () => {
    setLoading(true);
    api.adminGetSubmissions(filter || undefined).then(setSubs).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const save = async () => {
    await api.adminUpdateSubmission(editing, editForm);
    setEditing(null);
    load();
  };

  const STATUS_COLORS = { new: '', in_review: 'tag-gold', accepted: 'tag-teal', rejected: 'tag-red' };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Filmmaker Submissions</h2>
        <select className="form-input" style={{ maxWidth: 200, minWidth: 140 }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          {['new','in_review','accepted','rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {editing && (
        <div className={styles.userPanel}>
          <h3 className={styles.sectionTitle} style={{ marginBottom: 16 }}>Update Submission</h3>
          <div className={styles.formRow}>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {['new','in_review','accepted','rejected'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Admin notes (sent to filmmaker)</label>
            <textarea className="form-input" rows={3} value={editForm.adminNotes}
              onChange={e => setEditForm(f => ({ ...f, adminNotes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-magenta btn-sm" onClick={save}>Save</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {subs.map(s => (
            <div key={s.id} className={styles.submissionCard}>
              <div className={styles.submissionHeader}>
                <div>
                  <strong style={{ color: 'var(--white)', fontSize: 16 }}>{s.project_title}</strong>
                  <span className="label" style={{ marginLeft: 12, color: 'var(--gray)' }}>by {s.name}</span>
                </div>
                <span className={`tag ${STATUS_COLORS[s.status] || ''}`}>{s.status}</span>
              </div>
              <p style={{ fontSize: 14, color: 'var(--gray)', margin: '8px 0' }}>{s.logline}</p>
              <div className={styles.submissionMeta}>
                <span>{s.email}</span>
                <span>{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
              {s.admin_notes && (
                <div className="alert alert-info" style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>Notes:</strong> {s.admin_notes}
                </div>
              )}
              <button className="btn btn-outline-magenta btn-sm" style={{ marginTop: 12 }}
                onClick={() => { setEditing(s.id); setEditForm({ status: s.status, adminNotes: s.admin_notes || '' }); }}>
                Update Status
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AUDIT LOG TAB ────────────────────────────────────────────
function AdminAuditLog() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminGetAuditLog().then(setLog).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h2 className={styles.sectionTitle} style={{ marginBottom: 20 }}>Audit Log</h2>
      {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Admin</th><th>Action</th><th>Target</th><th>Details</th><th>When</th></tr></thead>
            <tbody>
              {log.map(l => (
                <tr key={l.id}>
                  <td style={{ color: 'var(--white)' }}>{l.admin_username}</td>
                  <td><span className="tag">{l.action}</span></td>
                  <td style={{ color: 'var(--gray)', fontSize: 13 }}>{l.target_type}</td>
                  <td style={{ color: 'var(--gray)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.details ? JSON.stringify(l.details).slice(0, 60) : '—'}
                  </td>
                  <td style={{ color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── EVENTS TAB ───────────────────────────────────────────────
function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', location: '', isVirtual: false,
    eventDate: '', minRewardTier: 0, trailerId: '',
  });

  const load = () => {
    setLoading(true);
    api.getEvents().then(setEvents).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = k => e => setForm(f => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const submit = async e => {
    e.preventDefault();
    setSaving(true); setMessage(null);
    try {
      await api.adminCreateEvent({
        ...form,
        minRewardTier: parseInt(form.minRewardTier),
        trailerId: form.trailerId || undefined,
      });
      setMessage({ type: 'success', text: 'Event created' });
      setShowForm(false);
      setForm({ title:'', description:'', location:'', isVirtual:false, eventDate:'', minRewardTier:0, trailerId:'' });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const TIER_LABELS = ['All supporters', 'Bronze+', 'Silver+', 'Gold+', 'Diamond'];

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Events</h2>
        <button className="btn btn-magenta btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancel' : '+ New Event'}
        </button>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className={styles.form}>
          <div className="form-group">
            <label className="form-label">Event title *</label>
            <input className="form-input" value={form.title} onChange={set('title')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" value={form.description} onChange={set('description')}
              rows={3} style={{ resize: 'vertical' }} />
          </div>
          <div className={styles.formRow}>
            <div className="form-group">
              <label className="form-label">Date & time *</label>
              <input className="form-input" type="datetime-local" value={form.eventDate}
                onChange={set('eventDate')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Min reward tier</label>
              <select className="form-input" value={form.minRewardTier} onChange={set('minRewardTier')}>
                {TIER_LABELS.map((l, i) => (
                  <option key={i} value={i}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" value={form.location} onChange={set('location')}
                placeholder="City, State or leave blank" />
            </div>
            <div className="form-group">
              <label className="form-label">Linked trailer ID (optional)</label>
              <input className="form-input" value={form.trailerId} onChange={set('trailerId')}
                placeholder="UUID" />
            </div>
          </div>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={form.isVirtual} onChange={set('isVirtual')} />
            Virtual event (online)
          </label>
          <button type="submit" className="btn btn-magenta" disabled={saving}
            style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Creating…' : 'Create Event'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : events.length === 0 ? (
        <div style={{ padding: '40px 0', color: 'var(--gray)', textAlign: 'center' }}>
          No upcoming events. Create one above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {events.map(ev => (
            <div key={ev.id} className={styles.submissionCard}>
              <div className={styles.submissionHeader}>
                <strong style={{ color: 'var(--white)', fontSize: 15 }}>{ev.title}</strong>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {ev.is_virtual
                    ? <span className="tag tag-teal">Virtual</span>
                    : <span className="tag">{ev.location}</span>
                  }
                  {ev.min_reward_tier > 0 && (
                    <span className="tag tag-gold">{TIER_LABELS[ev.min_reward_tier]} required</span>
                  )}
                </div>
              </div>
              {ev.description && (
                <p style={{ fontSize: 13, color: 'var(--gray)', margin: '8px 0 0', lineHeight: 1.5 }}>
                  {ev.description}
                </p>
              )}
              <div className={styles.submissionMeta}>
                <span>{new Date(ev.event_date).toLocaleString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
